'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { datadogRum } from '@datadog/browser-rum';

import {
  FlexipassDoor,
  GuestDTO,
  ReservationWithRateDetailsDTO,
  Settings,
} from '@/models';
import useLocalization from '@/hooks/useLocalization';
import { ClientFlexipassService } from '@/services/client';

import { DigitalKeysNotAvailable } from './components/DigitalKeysNotAvailable';
import { DigitalKeysAvailable } from './components/DigitalKeysAvailable';
import { getDigitalKeysAvailability } from './utils';

interface DigitalKeysProps {
  company: string;
  guestId: string | null;
  settings: Settings;
  reservation: ReservationWithRateDetailsDTO | null;
  preview?: boolean;
}

export const DigitalKeys = ({
  company,
  settings,
  guestId,
  reservation,
  preview = false,
}: DigitalKeysProps): React.ReactElement | null => {
  const intl = useIntl();
  const { dateFnsLocale } = useLocalization();
  const [loading, setLoading] = useState<boolean>(true);
  const [flexipassDoors, setFlexipassDoors] = useState<FlexipassDoor[]>([]);
  const reservationId = reservation?.id;
  const reservationCheckedOut =
    reservation &&
    ['checked_out', 'cancelled', 'no_show'].includes(reservation.status);

  const openFlexipassDoor = useCallback(
    async (doorId: number) => {
      return await ClientFlexipassService.openDoor(
        company,
        guestId!,
        reservationId!,
        doorId,
      );
    },
    [reservationId, company, guestId],
  );

  useEffect(() => {
    // Clear stale keys immediately when the request identity changes so a
    // previous reservation's doors can never render (or be opened) under a
    // new identity.  The catch block intentionally does NOT clear, so
    // same-identity transient poll failures keep the last successful list.
    setFlexipassDoors([]);
    setLoading(true);

    // cancelled is set to true in cleanup so any in-flight response that
    // arrives after the identity has changed is silently dropped, preventing
    // a stale reservation's doors from writing into the new identity's state.
    let cancelled = false;

    const fetchDoors = async () => {
      if (preview || !company || !guestId || !reservationId) {
        return;
      }

      try {
        const doors =
          await ClientFlexipassService.getDoorsByReservationAndGuest(
            company,
            guestId,
            reservationId,
          );
        if (!cancelled) {
          setFlexipassDoors(doors ?? []);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        if (!cancelled) {
          console.error('[flexipass] fetchDoors error:', error);
          datadogRum.addError(error, { feature: 'flexipass-doors' });
          // Keep the last successful list visible on a transient poll failure; do not wipe valid keys.
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDoors();

    const intervalId = setInterval(fetchDoors, 1000 * 30);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [preview, company, guestId, reservationId]);

  if (preview) {
    if (!settings.flexipass) {
      return null;
    }

    // preview Digital Keys component
    return (
      <DigitalKeysAvailable
        title={intl.formatMessage({
          id: 'digital_keys/digital_keys',
          defaultMessage: 'Digital Keys',
        })}
        type="flexipass"
        settings={settings}
        preview
      />
    );
  }

  const hasGuest = Boolean(guestId);
  if (
    loading ||
    !hasGuest ||
    !reservation ||
    reservationCheckedOut ||
    !settings.flexipass
  ) {
    // do not display Digital Keys component
    return null;
  }

  const isAvailable = flexipassDoors.find((door) => door.is_available);
  const { isPastCheckout, arrivingToday, formattedCheckInHours } =
    getDigitalKeysAvailability(
      settings.whistle_guest_company_timezone,
      settings.flexipass,
      reservation,
      dateFnsLocale,
    );

  if (isPastCheckout) {
    // do not display Digital Keys component
    return null;
  }

  if (!isAvailable) {
    return (
      <DigitalKeysNotAvailable
        noRoomAssigned={flexipassDoors.length === 0}
        defaultCheckInTime={formattedCheckInHours}
        arrivingToday={arrivingToday}
      />
    );
  }

  return (
    <DigitalKeysAvailable
      title={intl.formatMessage({
        id: 'digital_keys/digital_keys',
        defaultMessage: 'Digital Keys',
      })}
      type="flexipass"
      settings={settings}
      items={flexipassDoors}
      onDoorOpen={openFlexipassDoor}
    />
  );
};
